import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mdiEmoticonSadOutline } from "@mdi/js";

import AppBar from "components/AppBar";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import useNotifier from "hooks/useNotifier";
import EventApi from "api/event";
import EventAppsApi, { EventAppsRow } from "api/event-apps";
import VoteApi from "api/vote";
import ProfileApi from "api/profile";
import { Box, Grid } from "@mui/material";
import EventDetails from "containers/Event/EventDetails";
import useBoundStore from "store";
import EventWeather from "containers/Event/EventWeather";
import EventLocation from "containers/Event/EventLocation";
import EventStats from "containers/Event/EventStats";
import EventAppsMenu from "containers/Event/EventAppsMenu";

interface TimelineEventDetailsProps {
  onBack?: () => void;
  onEventAppsMenuItemClick: (route: string) => void;
  onViewParticipants: () => void;
  onViewPending: () => void;
  onViewVehicles: () => void;
}

function TimelineEventDetails(props: TimelineEventDetailsProps) {
  const { onBack, onEventAppsMenuItemClick, onViewParticipants, onViewPending, onViewVehicles } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [activeApps, setActiveApps] = useState<EventAppsRow["active_apps"] | null>(null);
  const navigate = useNavigate();
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const selected = useBoundStore((state) => state.selected);
  const { timelineEvent } = selected;
  const { showNotification } = useNotifier();
  const eventApi = useMemo(() => new EventApi(showNotification), [showNotification]);
  const eventAppsApi = useMemo(() => new EventAppsApi(showNotification), [showNotification]);
  const voteApi = useMemo(() => new VoteApi(showNotification), [showNotification]);
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);

  /**
   * Fetch event details
   */
  const fetchEvent = useCallback(async () => {
    if (timelineEvent?.id && !timelineEvent.event) {
      setIsLoading(true);

      const eventId = timelineEvent.id;
      const { event, error: eventError } = await eventApi.getEvent(eventId, "closed");
      const { votes, error: votesError } = await voteApi.getVotesByEventsIds([eventId]);
      const usersIds = votes.map((vote) => vote.uid);
      const { users, error: usersError } = await profileApi.getUsersByIds(usersIds);

      if (!eventError && !votesError && !usersError && event) {
        selected.set({ timelineEvent: { id: event.id, event, users, votes } });
      } else {
        setHasError(true);
      }

      setIsLoading(false);
    }
  }, [timelineEvent?.id]);

  /**
   * Check which apps are active for this timeline event
   */
  useEffect(() => {
    (async () => {
      if (timelineEvent?.event && selected.groupId) {
        const { data } = await eventAppsApi.getActiveApps(timelineEvent.event.id, selected.groupId);
        setActiveApps(data?.active_apps ?? []);
      }
    })();
  }, [timelineEvent?.event?.id, selected.groupId]);

  /**
   * Clear the value of the timeline event in store
   */
  function clearTimelineEvent(): void {
    selected.set({ timelineEvent: null });
  }

  /**
   * Renders app bar
   *
   * @returns
   */
  function renderAppBar(): JSX.Element {
    const actions: Array<React.ReactElement> = [];
    if (activeApps && activeApps.length > 0) {
      actions.push(<EventAppsMenu key='apps' onMenuItemSelect={onEventAppsMenuItemClick} visibleApps={activeApps} />);
    }

    return (
      <AppBar
        onBack={() => {
          if (onBack) {
            onBack();
          }
          if (isMobileDevice) {
            navigate(-1);
          }
          clearTimelineEvent();
        }}
        actions={actions}
        title='Details'
      />
    );
  }

  /**
   * On component mount, fetch event details
   */
  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  if (isLoading)
    return (
      <PageLayout appBar={renderAppBar()}>
        <PageLoader />
      </PageLayout>
    );

  if (hasError) {
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder text='Ups! Something went wrong' icon={mdiEmoticonSadOutline} />
      </PageLayout>
    );
  }

  return (
    <PageLayout appBar={renderAppBar()}>
      <Box p={2}>
        <Grid container spacing={2}>
          {timelineEvent?.event ? (
            <Grid item xs={12}>
              <EventDetails event={timelineEvent?.event} />
            </Grid>
          ) : null}

          {timelineEvent?.event ? (
            <Grid item xs={12}>
              <EventStats
                users={timelineEvent?.users ?? []}
                event={timelineEvent?.event}
                votes={timelineEvent?.votes ?? []}
                onViewParticipants={onViewParticipants}
                onViewPending={onViewPending}
                onViewVehicles={onViewVehicles}
              />
            </Grid>
          ) : null}

          {timelineEvent?.event?.location ? (
            <Grid item xs={12}>
              <EventLocation location={timelineEvent.event.location} />
            </Grid>
          ) : null}

          {timelineEvent?.event?.weather ? (
            <Grid item xs={12}>
              <EventWeather event={timelineEvent.event} />
            </Grid>
          ) : null}
        </Grid>
      </Box>
    </PageLayout>
  );
}

export default TimelineEventDetails;
